// src/panels/PanelLetter.jsx
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { SectionHeader,isDeadlinePassed } from '../components/SharedComponents';
import useToast from '../hooks/useToast';

const FALLBACK_LETTER_LIMIT = 2200;

function getLetterText(letter) {
  if (!letter) return '';
  if (typeof letter === 'string') return letter;
  return letter.data?.text || letter.text || '';
}

function normalizeLetters(letters, legacyText) {
  if (Array.isArray(letters) && letters.length > 0) {
    return letters
      .filter(Boolean)
      .map((letter, index) => ({
        ...letter,
        id: letter.id || letter.content_id || `letter-${index}`,
        data: {
          ...(letter.data || {}),
          text: getLetterText(letter),
        },
      }));
  }

  if ((legacyText || '').trim()) {
    return [
      {
        id: 'legacy-letter',
        data: { text: legacyText },
        isLegacy: true,
      },
    ];
  }

  return [];
}

function getPreview(text = '') {
  return text.trim() || 'No note yet.';
}

function unwrapErrorDetail(errorOrResponse) {
  const raw = errorOrResponse?.response?.data || errorOrResponse?.data || errorOrResponse || {};
  return raw?.detail && typeof raw.detail === 'object' ? raw.detail : raw;
}

function getLimitCode(errorOrResponse) {
  const detail = unwrapErrorDetail(errorOrResponse);
  return String(detail?.code || errorOrResponse?.code || '').toUpperCase();
}

function getLimitKey(errorOrResponse) {
  const detail = unwrapErrorDetail(errorOrResponse);
  return String(detail?.limitKey || detail?.limit_key || '').toLowerCase();
}

function isNoteCountLimitError(errorOrResponse) {
  const detail = unwrapErrorDetail(errorOrResponse);
  const code = getLimitCode(errorOrResponse);
  const limitKey = getLimitKey(errorOrResponse);
  const source = String(detail?.source || '').toLowerCase();
  const message = String(detail?.message || errorOrResponse?.message || '').toLowerCase();

  return (
    code === 'PERSONAL_NOTES_LIMIT_EXCEEDED' ||
    code === 'LETTER_COUNT_LIMIT_EXCEEDED' ||
    (code === 'LIMIT_EXCEEDED' && ['letter_count', 'letters', 'personal_notes', 'letter_notes'].includes(limitKey)) ||
    (source === 'letter' && message.includes('limit')) ||
    message.includes('personal note limit')
  );
}

function isLetterCharacterLimitError(errorOrResponse) {
  const code = getLimitCode(errorOrResponse);
  const limitKey = getLimitKey(errorOrResponse);
  const message = String(unwrapErrorDetail(errorOrResponse)?.message || errorOrResponse?.message || '').toLowerCase();

  return (
    code === 'LETTER_TOO_LONG' ||
    code === 'LETTER_CHARACTER_LIMIT_EXCEEDED' ||
    (code === 'LIMIT_EXCEEDED' && ['letter_chars', 'letter_characters', 'personal_note_chars'].includes(limitKey)) ||
    message.includes('character limit') ||
    message.includes('too long')
  );
}

export default function PanelLetter({
  letters = [],
  text = '',
  onAddLetter,
  onUpdateLetter,
  onDeleteLetter,
  onSave,
  limit = 0,
  letterCountLimit = 1,
  onUpgrade,
  bookConfig,
  childName = 'your child',
}) {
  const toast = useToast();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingLetter, setEditingLetter] = useState(null);
  const [draftText, setDraftText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

const isViewOnly = useMemo(
  () => isDeadlinePassed(bookConfig?.content_deadline),
  [bookConfig?.content_deadline]
);

  const letterLimit = Number.isFinite(Number(limit)) && Number(limit) > 0
    ? Number(limit)
    : FALLBACK_LETTER_LIMIT;

  const normalizedLetters = useMemo(
    () => normalizeLetters(letters, text),
    [letters, text]
  );

  const hasLetters = normalizedLetters.length > 0;
  const maxLetterCount = Number.isFinite(Number(letterCountLimit)) && Number(letterCountLimit) > 0
    ? Number(letterCountLimit)
    : 1;
  const draftCount = draftText.length;
  const isOverLimit = draftCount > letterLimit;
  const isAtLimit = draftCount >= letterLimit;
  const canSave = draftText.trim().length > 0 && !isSaving;

  const openLetterLimitUpgrade = (errorOrResponse) => {
    const detail = unwrapErrorDetail(errorOrResponse);

    onUpgrade?.({
      source: 'letter',
      reason: 'letter_limit',
      currentLimit: detail?.limit || letterLimit,
      current: detail?.current,
      remaining: detail?.remaining,
      message: detail?.message,
      upgrade: detail?.upgrade,
      available_offers: detail?.upgrade?.available_offers || detail?.available_offers,
    });
  };

  const openLetterCountUpgrade = (errorOrResponse) => {
    const detail = unwrapErrorDetail(errorOrResponse);

    onUpgrade?.({
      source: 'letter',
      reason: 'letter_count_limit',
      currentLimit: detail?.limit || maxLetterCount,
      current: detail?.current,
      remaining: detail?.remaining,
      message: detail?.message,
      upgrade: detail?.upgrade,
      available_offers: detail?.upgrade?.available_offers || detail?.available_offers,
    });
  };

  const openAddEditor = () => {
    if (isViewOnly) return;
    setEditingLetter(null);
    setDraftText('');
    setEditorOpen(true);
  };

  const openEditEditor = (letter) => {
    if (isViewOnly) return;
    setEditingLetter(letter);
    setDraftText(getLetterText(letter));
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (isSaving) return;
    setEditorOpen(false);
    setEditingLetter(null);
    setDraftText('');
  };

  const handleDraftChange = (event) => {
    setDraftText(event.target.value || '');
  };

  const handleSave = async () => {
    if (!canSave) return;

    setIsSaving(true);
    try {
      let result;

      if (editingLetter?.isLegacy && onSave) {
        result = await onSave(draftText);
      } else if (editingLetter?.id) {
        result = await onUpdateLetter?.(editingLetter.id, draftText);
      } else {
        result = await onAddLetter?.(draftText);
      }

      if (result?.ok === false) {
        const error = result.error || result;

        if (isNoteCountLimitError(error)) {
          openLetterCountUpgrade(error);
          return;
        }

        if (isLetterCharacterLimitError(error)) {
          openLetterLimitUpgrade(error);
          return;
        }

        throw error || new Error('Save failed');
      }

      toast.success(editingLetter ? 'Note updated.' : 'Note added.');
      setEditorOpen(false);
      setEditingLetter(null);
      setDraftText('');
    } catch (err) {
      console.error('Failed to save note:', err);

      if (isNoteCountLimitError(err)) {
        openLetterCountUpgrade(err);
      } else if (isLetterCharacterLimitError(err)) {
        openLetterLimitUpgrade(err);
      } else {
        toast.error('Could not save note. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (letter) => {
    if (isViewOnly || !letter?.id || letter.isLegacy || deletingId) return;

    setDeletingId(letter.id);
    try {
      const result = await onDeleteLetter?.(letter.id);
      if (result?.ok === false) {
        throw result.error || new Error('Delete failed');
      }
      toast.success('Note deleted.');
    } catch (err) {
      console.error('Failed to delete note:', err);
      toast.error('Could not delete note. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      <SectionHeader
        title="Add a personal note"
        badge={`${normalizedLetters.length}/${maxLetterCount} added`}
        subtitle={
          isViewOnly ? (
            'The deadline has passed. Your notes are locked for printing.'
          ) : (
            <>
              Write something warm for{' '}
              <Box component="strong" sx={{ color: 'text.primary', fontWeight: 800 }}>
                {childName}
              </Box>
              {' '}— a favorite memory, something they love right now, or a wish you want them to remember.
            </>
          )
        }
      />

      {hasLetters ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          {normalizedLetters.map((letter, index) => {
            const letterText = getLetterText(letter);
            const isDeleting = deletingId === letter.id;

            return (
              <Box
                key={letter.id}
                sx={{
                  bgcolor: 'grey.50',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '6px',
                  px: { xs: 2, sm: 2.35 },
                  py: { xs: 1.8, sm: 2 },
                  opacity: isDeleting ? 0.5 : 1,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                  <Typography
                    sx={{
                      minWidth: 0,
                      flex: 1,
                      fontSize: 16,
                      lineHeight: 1.7,
                      color: 'text.primary',
                      display: '-webkit-box',
                      WebkitLineClamp: 5,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {getPreview(letterText)}
                  </Typography>

                  {!isViewOnly && (
                    <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                      <IconButton size="small" onClick={() => openEditEditor(letter)} aria-label={`Edit note ${index + 1}`}>
                        <EditOutlinedIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                      {!letter.isLegacy && (
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(letter)}
                          disabled={Boolean(deletingId)}
                          aria-label={`Delete note ${index + 1}`}
                          sx={{ '&:hover': { color: 'error.main', bgcolor: 'transparent' } }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 21 }} />
                        </IconButton>
                      )}
                    </Stack>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>
      ) : (
        <Box
          sx={{
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '6px',
            px: { xs: 2, sm: 2.35 },
            py: { xs: 2, sm: 2.35 },
          }}
        >
          <Typography sx={{ fontSize: 16, color: 'text.secondary' }}>No note added yet.</Typography>
        </Box>
      )}

      {!isViewOnly && (
        <Button
          variant="text"
          startIcon={<AddIcon />}
          onClick={openAddEditor}
          sx={{
            mt: 2.25,
            px: 0,
            color: 'text.primary',
            textTransform: 'none',
            fontWeight: 850,
            '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
          }}
        >
          Add note
        </Button>
      )}

      <Dialog open={editorOpen} onClose={closeEditor} fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: '6px' } }}>
        <DialogTitle sx={{ px: { xs: 2.5, sm: 3 }, pt: 2.5, pb: 1.25 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography sx={{ fontSize: 22, fontWeight: 850 }}>
              {editingLetter ? 'Edit note' : 'Add note'}
            </Typography>
            <IconButton onClick={closeEditor} size="small" disabled={isSaving} aria-label="Close note editor">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: 1, pb: 0 }}>
          <TextField
            autoFocus
            multiline
            minRows={10}
            maxRows={18}
            fullWidth
            value={draftText}
            onChange={handleDraftChange}
            placeholder={`Dear ${childName},\n\nOne thing I loved watching this year was...`}
            error={isOverLimit}
            sx={softTextareaSx}
          />

          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2} sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 12.5, color: isAtLimit ? 'error.main' : 'text.secondary', fontWeight: 700 }}>
              {draftCount.toLocaleString()} / {letterLimit.toLocaleString()}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
          <Button onClick={closeEditor} disabled={isSaving} color="inherit" sx={{ textTransform: 'none', fontWeight: 700 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!canSave}
            sx={{ borderRadius: '6px', textTransform: 'none', fontWeight: 850, px: 2.5 }}
          >
            {isSaving ? 'Saving...' : 'Save note'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const softTextareaSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    bgcolor: 'grey.50',
    fontSize: 16,
    lineHeight: 1.75,
    alignItems: 'flex-start',
    px: 1.75,
    py: 1.5,
    '& fieldset': { border: 'none' },
    '&:hover fieldset': { border: 'none' },
    '&.Mui-focused fieldset': { border: 'none' },
  },
  '& textarea': {
    minHeight: 300,
  },
};